import {
  BookOpen,
  Cable,
  Cpu,
  Info,
  ListVideo,
  Radio,
  Workflow,
} from "lucide-react";
import { useSingleStudio } from "@/hooks/useStudios";
import { useStudioConnections } from "@/hooks/useStudioConnections";
import { useBridgeStatus } from "@/hooks/useBridgeConnections";
import { matchContextPath } from "../match";
import type { ContextParams, NavContextDef, NavItem } from "../types";

export const studioPath = (id: string) => `/studios/${id}`;

/**
 * Devices badge (L6): connected/total from the bridge when it answers,
 * otherwise the number of devices. Both are shared with the page's queries.
 */
function useDevicesBadge(id: string): string | number | null {
  const { data: studio } = useSingleStudio(id);
  const { data: bridgeStatus } = useBridgeStatus(studio?.bridge_url);
  const { data: connections, isLoading } = useStudioConnections(id);
  if (bridgeStatus?.connections) {
    return `${bridgeStatus.connections.connected}/${bridgeStatus.connections.total}`;
  }
  return isLoading ? null : connections.length;
}

function useStudioItems({ id }: ContextParams): NavItem[] {
  const base = studioPath(id);
  const devicesBadge = useDevicesBadge(id);
  return [
    {
      key: "information",
      label: "Information",
      href: base,
      exact: true,
      icon: Info,
      testId: "tab-information",
    },
    {
      key: "devices",
      label: "Devices",
      href: `${base}/devices`,
      icon: Cpu,
      badge: devicesBadge,
      testId: "tab-connections",
    },
    {
      key: "wiring",
      label: "Wiring",
      href: `${base}/wiring`,
      icon: Cable,
      testId: "tab-wiring",
    },
    {
      key: "configurations",
      label: "Configurations",
      href: `${base}/configurations`,
      icon: Workflow,
      testId: "tab-configurations",
    },
    {
      key: "playout",
      label: "Playout",
      href: `${base}/playout/playlists`,
      activePrefix: `${base}/playout`,
      icon: ListVideo,
      testId: "tab-playout",
      children: [
        {
          key: "playout/playlists",
          label: "Playlists",
          href: `${base}/playout/playlists`,
          testId: "playout-tab-playlists",
        },
        {
          key: "playout/playouts",
          label: "Playouts",
          href: `${base}/playout/playouts`,
          testId: "playout-tab-playouts",
        },
        {
          key: "playout/artwork",
          label: "Artwork",
          href: `${base}/playout/artwork`,
          testId: "playout-tab-artwork",
        },
        {
          key: "playout/log",
          label: "Log",
          href: `${base}/playout/log`,
          testId: "playout-tab-log",
        },
        {
          key: "playout/settings",
          label: "Settings",
          href: `${base}/playout/settings`,
          testId: "playout-tab-settings",
        },
      ],
    },
    {
      key: "docs",
      label: "Docs",
      href: `${base}/docs`,
      icon: BookOpen,
      testId: "tab-docs",
    },
  ];
}

function useStudioHeader({ id }: ContextParams) {
  const { data: studio } = useSingleStudio(id);
  if (!studio) return {};
  return { kicker: studio.number ?? null, title: studio.name };
}

export const studioContext: NavContextDef = {
  type: "studio",
  typeLabel: "Studio",
  icon: Radio,
  match: (pathname) => {
    // A config is its own context (D14).
    if (matchContextPath("/studios/:studioId/config/:configId", pathname)) {
      return null;
    }
    return matchContextPath("/studios/:id", pathname, { id: ["new"] });
  },
  key: ({ id }) => `studio:${id}`,
  base: ({ id }) => studioPath(id),
  parent: () => ({ label: "Studios", href: "/studios" }),
  useHeader: useStudioHeader,
  useItems: useStudioItems,
};
